import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Save as SaveIcon,
  PlayArrow as TestIcon,
  Publish as ReleaseIcon,
  ArrowBack as BackIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Flag as FlagIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import RichTextDisplay from '../components/RichTextDisplay';
import api from '../api/axios';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type {
  EntityType,
  PromptVersion,
  ActiveVersionFeedback,
  EntityFeedbackResponse,
  VersionFeedback,
  ReasoningEffort,
} from '../types/prompts';

interface EntityTypesResponse {
  count: number;
  results: EntityType[];
}

export default function PromptVersionEditor() {
  const navigate = useNavigate();
  const { versionId } = useParams<{ versionId: string }>();
  const [searchParams] = useSearchParams();
  const isNew = versionId === 'new';

  // Form state
  const [entityTypes, setEntityTypes] = useState<EntityType[]>([]);
  const [selectedEntityType, setSelectedEntityType] = useState(
    searchParams.get('entity_type') || ''
  );
  const [promptText, setPromptText] = useState('');
  const [notes, setNotes] = useState('');
  const [reasoningEffort, setReasoningEffort] = useState<ReasoningEffort>('medium');

  // Version state
  const [version, setVersion] = useState<PromptVersion | null>(null);
  const [feedback, setFeedback] = useState<ActiveVersionFeedback | null>(null);
  const [allVersionsFeedback, setAllVersionsFeedback] = useState<VersionFeedback[]>([]);
  const [expandedFeedback, setExpandedFeedback] = useState<string[]>([]);
  const [expandedVersions, setExpandedVersions] = useState<string[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [feedbackCountForDelete, setFeedbackCountForDelete] = useState<number>(0);
  const [deleting, setDeleting] = useState(false);

  // Load entity types
  useEffect(() => {
    api.get<EntityTypesResponse>('/schemes/prompts/entity-types/')
      .then((res) => {
        const types = res.data.results || res.data;
        setEntityTypes(Array.isArray(types) ? types : []);
      })
      .catch((err) => console.error('Failed to load entity types:', err));
  }, []);

  // Load existing version or template
  useEffect(() => {
    if (isNew) {
      // Load template from active version if entity type is selected
      if (selectedEntityType) {
        setLoading(true);
        // First find the active version ID, then fetch its full details
        api.get<{ results: { id: string; is_active: boolean }[] }>('/schemes/prompts/versions/', {
          params: { entity_type: selectedEntityType }
        })
          .then(async (res) => {
            const results = res.data.results || [];
            const activeVersion = results.find(v => v.is_active);
            if (activeVersion) {
              // Fetch full details including prompt_text
              const detailRes = await api.get<PromptVersion>(
                `/schemes/prompts/versions/${activeVersion.id}/`
              );
              // Copy prompt text and settings, NOT notes (notes should be fresh for new version)
              setPromptText(detailRes.data.prompt_text);
              setReasoningEffort(detailRes.data.reasoning_effort || 'medium');
              setNotes('');
            }
          })
          .catch((err) => console.error('Failed to load template:', err))
          .finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    } else {
      // Load existing version
      api.get<PromptVersion>(`/schemes/prompts/versions/${versionId}/`)
        .then((res) => {
          setVersion(res.data);
          setSelectedEntityType(res.data.entity_type_slug);
          setPromptText(res.data.prompt_text);
          setReasoningEffort(res.data.reasoning_effort || 'medium');
          setNotes(res.data.notes);
        })
        .catch((err) => {
          console.error('Failed to load version:', err);
          setError('Failed to load prompt version');
        })
        .finally(() => setLoading(false));
    }
  }, [versionId, isNew, selectedEntityType]);

  // Load feedback
  useEffect(() => {
    if (!isNew && versionId) {
      // For existing versions, fetch feedback for this specific version
      api.get<ActiveVersionFeedback>(`/schemes/prompts/versions/${versionId}/feedback/`)
        .then((res) => setFeedback(res.data))
        .catch((err) => console.error('Failed to load feedback:', err));
    }
  }, [versionId, isNew]);

  // Always load all versions' feedback for reference (for new versions or drafts)
  useEffect(() => {
    const entityType = isNew ? selectedEntityType : version?.entity_type_slug;
    if (!entityType) return;

    // Load all versions' feedback for this entity type
    api.get<EntityFeedbackResponse>(`/schemes/prompts/versions/feedback-by-entity/${entityType}/`)
      .then((res) => {
        setAllVersionsFeedback(res.data.versions || []);
        // Auto-expand the first version (most recent) if none expanded
        if (res.data.versions?.length > 0 && expandedVersions.length === 0) {
          setExpandedVersions([res.data.versions[0].version]);
        }
      })
      .catch((err) => console.error('Failed to load all feedback:', err));
  }, [isNew, selectedEntityType, version?.entity_type_slug]);

  const handleSave = async () => {
    if (!selectedEntityType || !promptText.trim()) {
      setError('Please select an entity type and provide prompt text');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (isNew) {
        // Create new version
        const response = await api.post<PromptVersion>('/schemes/prompts/versions/', {
          entity_type_slug: selectedEntityType,
          prompt_text: promptText,
          reasoning_effort: reasoningEffort,
          notes,
        });
        setDirty(false);
        navigate(`/schemes/prompts/editor/${response.data.id}`);
      } else {
        // Update existing version
        await api.patch<PromptVersion>(`/schemes/prompts/versions/${versionId}/`, {
          prompt_text: promptText,
          reasoning_effort: reasoningEffort,
          notes,
        });
        setDirty(false);
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setError(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (dirty) {
      await handleSave();
    }
    if (version?.id || !isNew) {
      navigate(`/schemes/prompts/test/${versionId}`);
    }
  };

  const handleRelease = async () => {
    if (!versionId || isNew) return;

    try {
      await api.post(`/schemes/prompts/versions/${versionId}/release/`);
      navigate('/schemes/evaluate?tab=entity-types');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to release');
    }
  };

  const handleDeleteClick = async () => {
    if (!versionId || isNew) return;

    // Fetch feedback count for this version
    try {
      const res = await api.get<{ feedback: { press_release_id: string }[] }>(
        `/schemes/prompts/versions/${versionId}/feedback/`
      );
      setFeedbackCountForDelete(res.data.feedback?.length || 0);
    } catch (err) {
      setFeedbackCountForDelete(0);
    }

    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!versionId || isNew) return;
    setDeleting(true);

    try {
      await api.delete(`/schemes/prompts/versions/${versionId}/`);
      setDeleteDialogOpen(false);
      navigate('/schemes/evaluate?tab=entity-types');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to delete');
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const toggleFeedbackExpanded = (pressReleaseId: string) => {
    setExpandedFeedback(prev =>
      prev.includes(pressReleaseId)
        ? prev.filter(id => id !== pressReleaseId)
        : [...prev, pressReleaseId]
    );
  };

  const toggleVersionExpanded = (versionStr: string) => {
    setExpandedVersions(prev =>
      prev.includes(versionStr)
        ? prev.filter(v => v !== versionStr)
        : [...prev, versionStr]
    );
  };

  // Render a single feedback item with output
  const renderFeedbackItem = (item: ActiveVersionFeedback['feedback'][0], keyPrefix: string = '') => (
    <Card key={`${keyPrefix}${item.press_release_id}`} sx={{ mb: 1 }} variant="outlined">
      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
          }}
          onClick={() => toggleFeedbackExpanded(`${keyPrefix}${item.press_release_id}`)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {item.is_correct === true && <CheckIcon fontSize="small" color="success" />}
            {item.is_correct === false && <CancelIcon fontSize="small" color="error" />}
            {item.is_correct === null && <CancelIcon fontSize="small" color="disabled" />}
            {item.flagged && <FlagIcon fontSize="small" color="warning" />}
            <Typography
              variant="body2"
              noWrap
              sx={{
                maxWidth: 200,
                '&:hover': { textDecoration: 'underline' },
              }}
              component="a"
              href={`/schemes/evaluate/${item.press_release_id}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              {item.case_name}
            </Typography>
          </Box>
          <IconButton size="small">
            {expandedFeedback.includes(`${keyPrefix}${item.press_release_id}`)
              ? <ExpandLessIcon fontSize="small" />
              : <ExpandMoreIcon fontSize="small" />
            }
          </IconButton>
        </Box>
        <Collapse in={expandedFeedback.includes(`${keyPrefix}${item.press_release_id}`)}>
          <Box sx={{ mt: 1 }}>
            {item.output ? (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Extraction Output:
                </Typography>
                <Box sx={{
                  mt: 0.5,
                  p: 1,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  fontSize: '0.8rem',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  maxHeight: 200,
                  overflow: 'auto'
                }}>
                  {typeof item.output === 'string'
                    ? item.output
                    : JSON.stringify(item.output, null, 2)
                  }
                </Box>
              </Box>
            ) : null}
            {item.correction ? (
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="error.main" fontWeight={600}>
                  Correction:
                </Typography>
                <Typography variant="body2" sx={{ ml: 1 }}>
                  {item.correction}
                </Typography>
              </Box>
            ) : null}
            {item.flagged && item.flag_notes ? (
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="warning.main" fontWeight={600}>
                  Flag Note:
                </Typography>
                <Typography variant="body2" sx={{ ml: 1 }}>
                  {item.flag_notes}
                </Typography>
              </Box>
            ) : null}
            {item.notes ? (
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Notes:
                </Typography>
                <Typography variant="body2" sx={{ ml: 1 }}>
                  {item.notes}
                </Typography>
              </Box>
            ) : null}
            {!item.output && !item.correction && !item.flag_notes && !item.notes ? (
              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                No feedback
              </Typography>
            ) : null}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  const statusColor = version?.status === 'released' ? 'success' :
                      version?.status === 'draft' ? 'warning' : 'default';

  // Only drafts and new versions are editable
  const isEditable = isNew || version?.status === 'draft';

  return (
    <Layout>
      {/* Header */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate('/schemes/evaluate?tab=entity-types')} size="small">
              <BackIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="h5" fontWeight={600}>
                {isNew
                  ? (entityTypes.find(et => et.slug === selectedEntityType)?.label || 'Select Entity Type')
                  : version?.entity_type_name || version?.entity_type_slug
                }
              </Typography>
              {version ? (
                <>
                  <Chip label={version.version} size="small" variant="outlined" />
                  <Chip label={version.status} size="small" color={statusColor as 'success' | 'warning' | 'default'} />
                </>
              ) : isNew && selectedEntityType ? (
                <Chip label="Draft" size="small" color="warning" />
              ) : null}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isEditable && (
              <Button
                variant="outlined"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving || !dirty}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            )}
            {isEditable && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<TestIcon />}
                onClick={handleTest}
                disabled={isNew && !selectedEntityType}
              >
                Run Test
              </Button>
            )}
            {!isEditable && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate(`/schemes/prompts/editor/new?entity_type=${version?.entity_type_slug}`)}
              >
                Create New Version
              </Button>
            )}
            {!isNew && !version?.is_active && (
              <Button
                variant="contained"
                color="success"
                startIcon={<ReleaseIcon />}
                onClick={handleRelease}
              >
                {version?.status === 'archived' ? 'Re-activate' : 'Release'}
              </Button>
            )}
            {!isNew && !version?.is_active && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteClick}
              >
                Delete
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!isEditable && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This version is {version?.status}. Create a new version to make changes.
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2 }}>
        {/* Editor Panel */}
        <Box sx={{ flex: 2 }}>
          <Paper sx={{ p: 2 }}>
            {/* Entity Type Selector (only for new) */}
            {isNew && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Entity Type</InputLabel>
                <Select
                  value={selectedEntityType}
                  label="Entity Type"
                  onChange={(e) => {
                    setSelectedEntityType(e.target.value);
                    setDirty(true);
                  }}
                >
                  {entityTypes.map((et) => (
                    <MenuItem key={et.slug} value={et.slug}>
                      {et.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Settings Row */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Reasoning Effort</InputLabel>
                <Select
                  value={reasoningEffort}
                  label="Reasoning Effort"
                  onChange={(e) => {
                    setReasoningEffort(e.target.value as ReasoningEffort);
                    setDirty(true);
                  }}
                  disabled={!isEditable}
                >
                  <MenuItem value="none">None (fastest)</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High (slowest)</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Prompt Text */}
            <Typography variant="subtitle2" gutterBottom>
              Prompt Text
            </Typography>
            <TextField
              multiline
              fullWidth
              minRows={20}
              maxRows={40}
              value={promptText}
              onChange={(e) => {
                setPromptText(e.target.value);
                setDirty(true);
              }}
              placeholder="Enter your extraction prompt here..."
              disabled={!isEditable}
              sx={{
                '& .MuiInputBase-root': {
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                },
              }}
            />

            {/* Notes */}
            {(isEditable || notes) && (
              <>
                <Divider sx={{ my: 2 }} />
                {isEditable ? (
                  <TextField
                    fullWidth
                    label="Version Notes"
                    value={notes}
                    onChange={(e) => {
                      setNotes(e.target.value);
                      setDirty(true);
                    }}
                    placeholder="What changed in this version?"
                    multiline
                    rows={2}
                  />
                ) : (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Version Notes
                    </Typography>
                    <Typography variant="body2">
                      {notes}
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Paper>
        </Box>

        {/* Feedback Panel */}
        <Box sx={{ flex: 1, minWidth: 350, maxWidth: 450 }}>
          <Paper sx={{ p: 2, maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              {isNew ? 'Previous Version Feedback' : 'Version Feedback'}
            </Typography>

            {/* For existing non-draft versions - show this version's feedback first */}
            {!isNew && !isEditable && feedback?.active_version && (
              <>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  This Version ({feedback.active_version.version})
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {feedback.feedback.length} press release{feedback.feedback.length !== 1 ? 's' : ''} with feedback
                </Typography>
                <Box sx={{ mt: 1, mb: 2 }}>
                  {feedback.feedback.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No evaluation feedback yet.
                    </Typography>
                  ) : (
                    feedback.feedback.map((item) => renderFeedbackItem(item))
                  )}
                </Box>
                {allVersionsFeedback.length > 0 && <Divider sx={{ my: 2 }} />}
              </>
            )}

            {/* Show all versions' feedback - always available for reference */}
            {allVersionsFeedback.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No previous versions with feedback.
              </Typography>
            ) : (
              <>
                {(isNew || isEditable) && (
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Previous Versions
                  </Typography>
                )}
                {allVersionsFeedback
                  // For existing versions, filter out the current version from "previous" list
                  .filter(vf => isNew || vf.version !== version?.version)
                  .map((vf) => (
                    <Box key={vf.version} sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          cursor: 'pointer',
                          p: 1,
                          bgcolor: vf.is_active ? 'success.light' : 'grey.100',
                          borderRadius: 1,
                          mb: 1,
                        }}
                        onClick={() => toggleVersionExpanded(vf.version)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {vf.version}
                          </Typography>
                          <Chip
                            label={vf.is_active ? 'Active' : vf.status}
                            size="small"
                            color={vf.is_active ? 'success' : 'default'}
                            variant={vf.is_active ? 'filled' : 'outlined'}
                          />
                          {vf.feedback_summary.total > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              {vf.feedback_summary.correct}/{vf.feedback_summary.total} correct
                              {vf.feedback_summary.flagged > 0 && `, ${vf.feedback_summary.flagged} flagged`}
                            </Typography>
                          )}
                        </Box>
                        <IconButton size="small">
                          {expandedVersions.includes(vf.version)
                            ? <ExpandLessIcon fontSize="small" />
                            : <ExpandMoreIcon fontSize="small" />
                          }
                        </IconButton>
                      </Box>
                      <Collapse in={expandedVersions.includes(vf.version)}>
                        <Box sx={{ pl: 1 }}>
                          {vf.feedback.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                              No feedback for this version.
                            </Typography>
                          ) : (
                            vf.feedback.map((item) => renderFeedbackItem(item, `${vf.version}_`))
                          )}
                        </Box>
                      </Collapse>
                    </Box>
                  ))}
              </>
            )}
          </Paper>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Prompt Version</DialogTitle>
        <DialogContent>
          {version && (
            <>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to delete <strong>{version.entity_type_name} {version.version}</strong>?
              </Typography>
              {feedbackCountForDelete > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  This version has evaluation feedback on <strong>{feedbackCountForDelete} press release{feedbackCountForDelete !== 1 ? 's' : ''}</strong>.
                  Deleting this version will also delete all associated feedback.
                </Alert>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                This action cannot be undone.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
